<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue';
import { getLocalData, setLocalData } from '@/utils/localdata';
import { useI18n } from 'vue-i18n';
import axios from '@/plugins/axios.js';
import { useAlerts } from '@/store/alerts.js'
import Phone from '@/views/components/Phone.vue'
import phoneCode from '@/views/components/register/phoneCode.vue'
import { validateRecoverPass } from '@/utils/register';
import KiwishLoader from '@/views/components/KiwishLoader.vue';
import PasswordToggle from '@/views/components/PasswordToggle.vue';

const { locale } = useI18n();
const router = useRouter();
const loging = ref(false);
const emailPhoneError = ref('');
const passError = ref('');
const VUE_ASSETS_URL = process.env.VUE_APP_ASSETS_URL;
const APP_NAME = process.env.VUE_APP_NAME;
const lang = ref();

const phone = ref('');
const prefix = ref('');
const phoneCodeValue = ref('');
const password = ref('');
const confirmPass = ref('');
const step = ref(1);
const recovering = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const sendingPhoneCode = ref(false);

// Formato bonito del teléfono para mostrar
const formattedPhone = computed(() => {
  const digits = phone.value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0,3)} ${digits.slice(3)}`;
  return `${digits.slice(0,3)} ${digits.slice(3,6)} ${digits.slice(6)}`;
});

// Función para ajustar la altura del viewport considerando Safari iOS
const setViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

onMounted(async () => {
  // Configurar altura de viewport para Safari iOS
  setViewportHeight();
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', setViewportHeight);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', setViewportHeight);
  }

  step.value = 1;
  lang.value = await getLocalData('lang')
});

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('resize', setViewportHeight);
  window.removeEventListener('orientationchange', setViewportHeight);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', setViewportHeight);
  }
});

const sendPhoneCode = () => {
  sendingPhoneCode.value = true;
  // Asegurar que el teléfono no tenga espacios ni caracteres extraños
  const cleanPhone = phone.value.replace(/\D/g, '');
  axios.post('/recoverpass/phone-step', {phone: cleanPhone, prefix: prefix.value, lang: lang.value})
    .then (async r => {
      sendingPhoneCode.value = false;
      await nextTick();
      if (r.status == 200) {
        step.value++;
      }
    }).catch(e => {
      sendingPhoneCode.value = false;
      useAlerts().alert = {visible: true, message: e.response?.data?.exception ? 'register.msg.error.general' : e.response?.data?.message, class: 'alert-error'}
    })
}

const sendNewPass = () => {
  let validation = validateRecoverPass(phoneCodeValue.value, password.value, confirmPass.value);
  if (validation.password === true && validation.confirmPass === true && validation.phoneCode === true){
    recoverPass();
  }else{
    useAlerts().alert = {visible: true, message: 'msg.form.error', class: 'alert-error'}
    if (!validation.password) passError.value = 'register.msg.error.form.pass';
    if (!validation.confirmPass) passError.value = 'register.msg.error.form.pass_confirm';
  }
}

// Validación en tiempo real (onBlur)
const validatePass = () => {
  if (password.value && password.value.length < 8) {
    passError.value = 'register.msg.error.form.pass';
  }
};

const validateConfirmPass = () => {
  if (confirmPass.value && password.value !== confirmPass.value) {
    passError.value = 'register.msg.error.form.pass_confirm';
  }
};

const recoverPass = () => {
  // Prevenir doble submit
  if (recovering.value) return;

  passError.value = '';
  recovering.value = true;
  // Asegurar que el teléfono no tenga espacios ni caracteres extraños
  const cleanPhone = phone.value.replace(/\D/g, '');
  axios.post('/recoverpass/recover', {phone: cleanPhone, prefix: prefix.value, phoneCode: phoneCodeValue.value, password: password.value, confirmPass: confirmPass.value})
    .then (async r => {
      recovering.value = false;
      await nextTick();
      if (r.status == 200) {
        useAlerts().alert = {visible: true, message: 'recoverpass.success', class: 'alert-success'};
        router.push('/auth/login');
      } else {
        useAlerts().alert = {visible: true, message: r.data.message, class: 'alert-error'};
      }
    }).catch(e => {
      recovering.value = false;
      useAlerts().alert = {visible: true, message: e.response?.data?.message ? e.response?.data?.message : 'register.msg.error.general', class: 'alert-error'};
    })
}

</script>

<template>
  <ion-page class="bg-gradient-secrets">
    <!-- Logo header -->
    <div class="auth-logo-header">
      <img :src="VUE_ASSETS_URL + 'logokw_dark.png'"
           :srcset="VUE_ASSETS_URL + 'logokw_dark.png 1x, ' + VUE_ASSETS_URL + 'logokw_dark@2.png 2x'"
           alt="Kiwish" class="auth-logo" />
    </div>

    <div class="auth-wrapper">
      <div class="auth-container">
        <!-- Step 1: Phone input -->
        <div v-if="step==1" class="step-content">
          <p class="step-subtitle">{{ $t('recoverpass.subtitle1') }}</p>
          <div class="auth-input">
            <Phone v-model:phone="phone" v-model:prefix="prefix" />
          </div>
          <KiwishLoader :is-open="sendingPhoneCode" :message="$t('register.phone.sending')" />
        </div>

        <!-- Step 2: Code and new password -->
        <div v-if="step==2" class="step-content">
          <p class="step-label">{{ $t('recoverpass.phonecode') }}</p>
          <div class="phone-display">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ prefix }} {{ formattedPhone }}</span>
          </div>
          <div class="code-input-container">
            <phoneCode @updateCode="(value) => (phoneCodeValue = value)" />
          </div>

          <div class="auth-input">
            <ion-input
              v-model="password"
              @keydown="passError = ''"
              @keydown.enter.prevent="sendNewPass"
              @blur="validatePass"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('password')"
              enterkeyhint="next"
            />
            <PasswordToggle :visible="showPassword" @toggle="showPassword = !showPassword" />
          </div>
          <ion-text class="error-text" v-if="passError != ''">{{ $t(passError) }}</ion-text>

          <div class="auth-input">
            <ion-input
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model="confirmPass"
              @keydown="passError = ''"
              @keydown.enter.prevent="sendNewPass"
              @blur="validateConfirmPass"
              :placeholder="$t('register.password.confirm')"
              enterkeyhint="go"
            />
            <PasswordToggle :visible="showConfirmPassword" @toggle="showConfirmPassword = !showConfirmPassword" />
          </div>
          <KiwishLoader :is-open="recovering" :message="$t('register.recovering')" />
        </div>

        <!-- Links -->
        <div class="auth-links">
          <ion-text class="link-text">{{ $t('hasnot.account') }}</ion-text>
          <ion-text @click="router.replace('/auth/register')" class="link-action">{{ $t('register') }}</ion-text>
        </div>
        <div class="auth-links">
          <ion-text class="link-text">{{ $t('or') }}</ion-text>
          <ion-text @click="router.replace('/auth/login')" class="link-action">{{ $t('login') }}</ion-text>
        </div>

        <!-- Buttons -->
        <div class="button-container">
          <button class="btn-back" @click="step--" v-if="step == 2">
            <ion-icon icon="arrow-back"></ion-icon>
          </button>

          <button class="btn-primary" :class="{ 'btn-full': step == 1 }" v-if="step==1" :disabled="phone == '' || prefix == ''" @click="sendPhoneCode">{{ $t('continue') }}</button>
          <button class="btn-primary" v-if="step==2" :disabled="password == '' || confirmPass == '' || phoneCodeValue.length < 4" @click="sendNewPass">{{ $t('continue') }}</button>
        </div>
      </div>
    </div>

      </ion-page>
</template>

<style scoped>
ion-page {
  --background: transparent;
}

/* Logo header */
.auth-logo-header {
  display: flex;
  justify-content: center;
  padding-top: calc(env(safe-area-inset-top, 20px) + 20px);
  padding-bottom: 10px;
}

.auth-logo {
  height: 50px;
  width: auto;
}

.auth-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.auth-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 30px;
  padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 40px);
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.step-subtitle {
  font-size: 1rem;
  color: #5A695B;
  margin: 0 0 10px 0;
}

.step-label {
  font-size: 0.9rem;
  color: #5A695B;
  margin: 0 0 15px 0;
}

.code-input-container {
  margin-bottom: 20px;
}

/* Phone display */
.phone-display {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  gap: 10px;
  background: rgba(90, 105, 91, 0.15);
  padding: 14px 24px;
  border-radius: 12px;
  margin: 0 auto 20px auto;
  max-width: 280px;
}

.phone-display svg {
  flex-shrink: 0;
  color: #FF5287;
}

.phone-display span {
  font-size: 1.1rem;
  font-weight: 600;
  color: #5A695B;
  letter-spacing: 0.5px;
}

/* Inputs */
.auth-input {
  background: #EDEAD5;
  border: 1px solid #5A695B;
  border-radius: 16px;
  padding: 0 16px;
  min-height: 52px;
  display: flex;
  align-items: center;
}

.auth-input ion-input {
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --placeholder-color: #FF5287;
  --placeholder-opacity: 0.6;
  --color: #5A695B;
  font-size: 1rem;
  text-align: left;
}

/* Error */
.error-text {
  font-size: 0.75rem;
  color: #e74c3c;
  margin-left: 5px;
}

/* Links */
.auth-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  margin-top: 15px;
}

.link-text {
  font-size: 0.85rem;
  color: #5A695B;
}

.link-action {
  font-size: 0.85rem;
  font-weight: 700;
  color: #5A695B;
  text-decoration: underline;
  cursor: pointer;
}

/* Buttons */
.button-container {
  display: flex;
  gap: 10px;
  margin-top: 25px;
}

.btn-back {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid #FF5287;
  color: #FF5287;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-back ion-icon {
  font-size: 1.3rem;
}

.btn-primary {
  flex: 1;
  background: #FF5287;
  border: none;
  border-radius: 16px;
  color: #FFFFFF;
  font-size: 1rem;
  font-weight: 600;
  padding: 15px 30px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-primary.btn-full {
  width: 100%;
}

.btn-primary:active {
  opacity: 0.7;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

</style>